import React from "react";
import dayjs, { Dayjs } from "dayjs";
import { BarChart } from "@mui/x-charts/BarChart";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";

import WorkerCountPeriodTipsResponse from "../../Repositories/WorkerResponses/WorkerCountPeriodTipsResponse";
import WorkerWorkedDayResponse from "../../Repositories/WorkerResponses/WorkerWorkedDayResponse";
import WorkerCountPeriodDetailsDialog, { WorkerCountPeriodDetails } from "./WorkerCountPeriodDetailsDialog";

interface WorkerEarnedTipPerMonthProps {
    cashCountPeriods: WorkerCountPeriodTipsResponse[];
    pinCountPeriods: WorkerCountPeriodTipsResponse[];
    workDays: WorkerWorkedDayResponse[];
    startDate: Dayjs;
    endDate: Dayjs;
}

interface MonthlyTipData {
    key: string;
    label: string;
    cashTip: number;
    pinTip: number;
    countPeriods: WorkerCountPeriodDetails[];
}

const ACCENT = "#c79656";
const SURFACE = "#ffffff";
const BORDER = "rgba(0,0,0,0.07)";
const TEXT_PRIMARY = "#18181b";
const TEXT_SECONDARY = "rgba(24,24,27,0.52)";

function buildMonths(startDate: Dayjs, endDate: Dayjs) {
    const months: MonthlyTipData[] = [];
    let cursor = startDate.startOf("month");
    const last = endDate.startOf("month");
    while (cursor.isBefore(last) || cursor.isSame(last, "month")) {
        months.push({ key: cursor.format("YYYY-MM"), label: cursor.format("MMM"), cashTip: 0, pinTip: 0, countPeriods: [] });
        cursor = cursor.add(1, "month");
    }
    return months;
}

function formatCurrency(amount: number) {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(amount || 0);
}

function mergeCountPeriod(month: MonthlyTipData, period: WorkerCountPeriodTipsResponse, type: "cash" | "pin") {
    const key = `${period.countPeriodId}-${dayjs(period.targetStartDate).format("YYYY-MM-DD")}-${dayjs(period.targetEndDate).format("YYYY-MM-DD")}`;
    let countPeriod = month.countPeriods.find((item) => (
        `${item.countPeriodId}-${dayjs(item.targetStartDate).format("YYYY-MM-DD")}-${dayjs(item.targetEndDate).format("YYYY-MM-DD")}` === key
    ));

    if (!countPeriod) {
        countPeriod = {
            countPeriodId: period.countPeriodId,
            countPeriodStartDate: period.countPeriodStartDate,
            countPeriodEndDate: period.countPeriodEndDate,
            targetStartDate: period.targetStartDate,
            targetEndDate: period.targetEndDate,
            cashTip: 0,
            pinTip: 0,
        };
        month.countPeriods.push(countPeriod);
    }

    if (type === "cash") countPeriod.cashTip += period.cashTip ?? 0;
    if (type === "pin") countPeriod.pinTip += period.pinTip ?? 0;
}

export default function WorkerEarnedTipPerMonth({ cashCountPeriods, pinCountPeriods, workDays, startDate, endDate }: WorkerEarnedTipPerMonthProps) {
    const [selectedMonth, setSelectedMonth] = React.useState<MonthlyTipData | null>(null);
    const months = buildMonths(startDate, endDate);
    const monthMap = new Map(months.map((month) => [month.key, { ...month, countPeriods: [] as WorkerCountPeriodDetails[] }]));

    cashCountPeriods.forEach((period) => {
        const monthKey = dayjs(period.targetStartDate).startOf("month").format("YYYY-MM");
        const month = monthMap.get(monthKey);
        if (month) {
            month.cashTip += period.cashTip ?? 0;
            mergeCountPeriod(month, period, "cash");
        }
    });

    pinCountPeriods.forEach((period) => {
        const monthKey = dayjs(period.targetStartDate).startOf("month").format("YYYY-MM");
        const month = monthMap.get(monthKey);
        if (month) {
            month.pinTip += period.pinTip ?? 0;
            mergeCountPeriod(month, period, "pin");
        }
    });

    const monthlyData = months.map((month) => monthMap.get(month.key) ?? month);
    const monthsWithValues = monthlyData.filter((month) => month.cashTip > 0 || month.pinTip > 0);
    const totalCash = monthlyData.reduce((sum, month) => sum + month.cashTip, 0);
    const totalPin = monthlyData.reduce((sum, month) => sum + month.pinTip, 0);
    const totalTip = totalCash + totalPin;
    const strongestMonth = monthlyData.reduce((best, current) => {
        return (current.cashTip + current.pinTip) > ((best?.cashTip ?? 0) + (best?.pinTip ?? 0)) ? current : best;
    }, monthlyData[0]);

    return (
        <div style={{ borderRadius: 16, border: `1px solid ${BORDER}`, background: SURFACE, boxShadow: "0 2px 12px rgba(0,0,0,0.06)", overflow: "hidden" }}>
            {selectedMonth != null && (
                <WorkerCountPeriodDetailsDialog
                    open={selectedMonth != null}
                    monthKey={selectedMonth.key}
                    cashTip={selectedMonth.cashTip}
                    pinTip={selectedMonth.pinTip}
                    countPeriods={selectedMonth.countPeriods}
                    workDays={workDays}
                    onClose={() => setSelectedMonth(null)}
                />
            )}

            {/* Header */}
            <div style={{ padding: "22px 24px 20px", borderBottom: `1px solid ${BORDER}`, display: "flex", flexWrap: "wrap", gap: 20, alignItems: "flex-start", justifyContent: "space-between" }}>
                <div>
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.1, textTransform: "uppercase", color: TEXT_SECONDARY, marginBottom: 6 }}>Earned tip per month</div>
                    <div style={{ fontSize: 28, fontWeight: 800, color: TEXT_PRIMARY, letterSpacing: 0 }}>{formatCurrency(totalTip)}</div>
                    <div style={{ fontSize: 13, color: TEXT_SECONDARY, marginTop: 5, lineHeight: 1.5 }}>Cash and pin payouts grouped by month for the selected range.</div>
                </div>

                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <div style={{ borderRadius: 12, border: `1px solid ${BORDER}`, background: "#fafafa", padding: "12px 16px", display: "flex", flexDirection: "column", gap: 5, minWidth: 100 }}>
                        <PaidRoundedIcon style={{ fontSize: 16, color: ACCENT }} />
                        <span style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 700, color: TEXT_SECONDARY }}>Cash</span>
                        <span style={{ fontSize: 14, fontWeight: 700, color: TEXT_PRIMARY }}>{formatCurrency(totalCash)}</span>
                    </div>
                    <div style={{ borderRadius: 12, border: `1px solid ${BORDER}`, background: "#fafafa", padding: "12px 16px", display: "flex", flexDirection: "column", gap: 5, minWidth: 100 }}>
                        <CreditCardRoundedIcon style={{ fontSize: 16, color: TEXT_PRIMARY }} />
                        <span style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 700, color: TEXT_SECONDARY }}>Pin</span>
                        <span style={{ fontSize: 14, fontWeight: 700, color: TEXT_PRIMARY }}>{formatCurrency(totalPin)}</span>
                    </div>
                    <div style={{ borderRadius: 12, border: `1px solid ${BORDER}`, background: "#fafafa", padding: "12px 16px", display: "flex", flexDirection: "column", gap: 5, minWidth: 120 }}>
                        <TrendingUpRoundedIcon style={{ fontSize: 16, color: "#0f766e" }} />
                        <span style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 700, color: TEXT_SECONDARY }}>Best month</span>
                        <span style={{ fontSize: 14, fontWeight: 700, color: TEXT_PRIMARY }}>
                            {strongestMonth ? `${strongestMonth.label} · ${formatCurrency((strongestMonth.cashTip ?? 0) + (strongestMonth.pinTip ?? 0))}` : "No data"}
                        </span>
                    </div>
                </div>
            </div>

            {monthsWithValues.length === 0 ? (
                <div style={{ margin: 24, borderRadius: 12, border: `1px dashed rgba(199,150,86,0.30)`, background: "rgba(199,150,86,0.04)", padding: 28, color: TEXT_SECONDARY, fontSize: 14, lineHeight: 1.6 }}>
                    No tip history was returned for this worker in the selected period.
                </div>
            ) : (
                <>
                    {/* Chart */}
                    <div style={{ padding: "20px 16px 8px" }}>
                        <BarChart
                            height={260}
                            xAxis={[{ scaleType: "band", data: monthlyData.map((month) => month.label) }]}
                            series={[
                                { data: monthlyData.map((month) => Number(month.cashTip.toFixed(2))), label: "Cash", color: ACCENT, stack: "tips" },
                                { data: monthlyData.map((month) => Number(month.pinTip.toFixed(2))), label: "Pin", color: "#374151", stack: "tips" },
                            ]}
                            margin={{ left: 60, right: 20, top: 20, bottom: 32 }}
                            grid={{ horizontal: true }}
                        />
                    </div>

                    {/* Divider */}
                    <div style={{ height: 1, background: BORDER, margin: "0 24px" }} />

                    {/* Monthly breakdown */}
                    <div style={{ padding: "20px 24px 24px", display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(148px, 1fr))", gap: 10 }}>
                        {monthlyData.map((month) => {
                            const total = month.cashTip + month.pinTip;
                            const hasPayout = total > 0;
                            return (
                                <button
                                    key={month.key}
                                    type="button"
                                    onClick={() => setSelectedMonth(month)}
                                    style={{ appearance: "none", textAlign: "left", cursor: "pointer", borderRadius: 12, border: `1px solid ${hasPayout ? "rgba(199,150,86,0.22)" : BORDER}`, background: hasPayout ? "rgba(199,150,86,0.05)" : "#fafafa", padding: 14, display: "flex", flexDirection: "column", gap: 8, font: "inherit", transition: "border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease" }}
                                >
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 4 }}>
                                        <span style={{ fontSize: 13, fontWeight: 700, color: TEXT_PRIMARY }}>{dayjs(`${month.key}-01`).format("MMM YYYY")}</span>
                                        {hasPayout && (
                                            <span style={{ fontSize: 10, fontWeight: 700, color: ACCENT, background: "rgba(199,150,86,0.12)", borderRadius: 6, padding: "2px 7px" }}>Paid</span>
                                        )}
                                    </div>

                                    <div style={{ fontSize: 18, fontWeight: 800, color: hasPayout ? TEXT_PRIMARY : TEXT_SECONDARY, letterSpacing: 0 }}>
                                        {formatCurrency(total)}
                                    </div>

                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                                        <div style={{ borderRadius: 8, background: "rgba(0,0,0,0.04)", padding: "8px 10px" }}>
                                            <div style={{ fontSize: 10, fontWeight: 700, color: TEXT_SECONDARY, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 3 }}>Cash</div>
                                            <div style={{ fontSize: 12, fontWeight: 700, color: TEXT_PRIMARY }}>{formatCurrency(month.cashTip)}</div>
                                        </div>
                                        <div style={{ borderRadius: 8, background: "rgba(0,0,0,0.04)", padding: "8px 10px" }}>
                                            <div style={{ fontSize: 10, fontWeight: 700, color: TEXT_SECONDARY, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 3 }}>Pin</div>
                                            <div style={{ fontSize: 12, fontWeight: 700, color: TEXT_PRIMARY }}>{formatCurrency(month.pinTip)}</div>
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
}
